import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ProductDataComponent } from '../product-data/product-data.component';
import { LegalChecksComponent } from '../legal-checks/legal-checks.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InsuranceDetailsComponent } from '@base/components/insurance-details/insurance-details.component';
import { PersonDataComponent } from '@base/components/person-data/person-data.component';
import { PaymentDataComponent } from '@base/components/payment-data/payment-data.component';
import { checkForPaymentInfoInUrl, checkForPersonDataInUrl } from '@base/app/shared/utils/check-for-data-in-url.helper';
import { createElektronikForm, ElektronikFormModel } from '../../factories/tech-form.factory';
import { ElektronikService } from '../../services/elektronik.service';
import {
  BrokernetLanguageType,
  BrokernetProductName, Bundesland,
  PaymentType,
  PublicPersonCreateDto,
  PublicServoElektronikContractCreateDto,
  PublicServoElektronikLegalChecksDto,
  PublicServoElektronikProductDetailsDto
} from '@base/backend';
import { getAdvertiserInfo, getPublicLinkCustomerDto } from '@base/app/shared/utils/dto-builder.helper';
import { ServoProductNameTranslations } from '@base/models/enum/insurance-type.enum';

@Component({
  selector: 'app-elektronik-form-wrapper',
  standalone: true,
  imports: [
    ProductDataComponent,
    InsuranceDetailsComponent,
    PersonDataComponent,
    PaymentDataComponent,
    LegalChecksComponent,
    LegalChecksComponent,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './elektronik-form-wrapper.component.html',
  styleUrl: './elektronik-form-wrapper.component.scss'
})
export class ElektronikFormWrapperComponent implements OnInit {
  private readonly elektronikService = inject(ElektronikService);

  protected readonly InsuranceType = ServoProductNameTranslations;

  @Input() price!: number;
  @Input() premium!: number;
  @Input() servoAdvertiserId: string | null = null;
  @Input() isPublic: boolean = false;

  @Output() submitted = new EventEmitter<void>();

  isLoading = false;

  form!: FormGroup<ElektronikFormModel>;

  ngOnInit() {
    this.form = createElektronikForm();

    this.checkForDataInUrl();
  }

  checkForDataInUrl(): void {
    const url: URL = new URL(window.location.href);
    const personForm = this.form.controls.person.controls;
    const paymentForm = this.form.controls.payment.controls;

    checkForPersonDataInUrl(url, personForm);
    checkForPaymentInfoInUrl(url, paymentForm);
  }

  submit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;

    const dto: PublicServoElektronikContractCreateDto = {
      publicPerson: this.isPublic ? this.getPublicPersonCreateDto() : undefined,
      publicProductDetails: this.getProductDetailsDto(),
      publicLegalCheck: this.getLegalChecksDto(),
      publicBrokerAdvertiserInfo: this.isPublic ? getAdvertiserInfo(this.servoAdvertiserId) : undefined,
      publicLinkCustomerDto: this.isPublic ? undefined : getPublicLinkCustomerDto(),
    };

    this.elektronikService.submitElektronikContract(dto)
      .subscribe({
        next: () => this.submitted.emit(),
        complete: () => this.isLoading = false
      });
  }

  private getPublicPersonCreateDto(): PublicPersonCreateDto {
    const rawPersonData = this.form.controls.person.getRawValue();
    const paymentData = this.form.controls.payment.getRawValue();

    return {
      firstname: rawPersonData.firstname!,
      lastname: rawPersonData.lastname!,
      publicAddressCreateDto: {
        city: rawPersonData.address.city!,
        country: rawPersonData.address.country!,
        houseNumber: rawPersonData.address.houseNumber!,
        street: rawPersonData.address.street!,
        zip: rawPersonData.address.zip!,
        bundesland: Bundesland.Oberoesterreich
      },
      publicContactInfoCreateDto: {
        email: rawPersonData.email!,
        phone: rawPersonData.phone
      },
      publicPaymentInfoCreateDto: {
        iban: paymentData.iban!,
        paymentPeriod: paymentData.paymentPeriod!,
        paymentType: PaymentType.Sepa
      },
      brokernetLanguageType: BrokernetLanguageType.De
    }
  }

  private getProductDetailsDto(): PublicServoElektronikProductDetailsDto {
    const rawProductData = this.form.controls.product.getRawValue();

    return {
      brand: rawProductData.brand!,
      productGroup: rawProductData.productGroup!,
      model: rawProductData.model!,
      serialNumber: rawProductData.serialNumber!,
      publicServoElektronikVersicherungspraemieCalcDto: {
        kaufpreisInEur: this.price
      }
    };
  }

  private getLegalChecksDto(): PublicServoElektronikLegalChecksDto {
    const rawLegalChecksData = this.form.controls.legalCheck.getRawValue();

    return {
      ...rawLegalChecksData
    } as PublicServoElektronikLegalChecksDto;
  }

  protected readonly BrokernetProductName = BrokernetProductName;
}
